import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

export interface SkillsInputProps {
  id?: string;
  value: string[];
  onChange: (value: string[]) => void;
  /** Known skills (`GET /options` → `skill`); anything typed can be added too. */
  suggestions?: readonly string[];
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
}

/**
 * Multi-select with free text: chips for the chosen skills, an input that offers matching
 * suggestions and adds the typed text on Enter (or comma). Backspace on an empty input removes
 * the last chip.
 */
export function SkillsInput({
  id,
  value,
  onChange,
  suggestions = [],
  disabled = false,
  placeholder = 'Add a skill…',
  className,
  ...aria
}: SkillsInputProps) {
  const [text, setText] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const listId = React.useId();

  const add = (skill: string) => {
    const t = skill.trim();
    if (!t) return;
    if (!value.some((v) => v.toLowerCase() === t.toLowerCase())) onChange([...value, t]);
    setText('');
  };
  const remove = (skill: string) => onChange(value.filter((v) => v !== skill));

  const needle = text.trim().toLowerCase();
  const chosen = new Set(value.map((v) => v.toLowerCase()));
  const matches = suggestions
    .filter((s) => !chosen.has(s.toLowerCase()) && (!needle || s.toLowerCase().includes(needle)))
    .slice(0, 8);

  return (
    <div className={cn('space-y-2', className)}>
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label="Selected skills">
          {value.map((skill) => (
            <li key={skill}>
              <Badge variant="secondary" className="gap-1 pr-1 font-normal">
                {skill}
                {disabled ? null : (
                  <button
                    type="button"
                    className="rounded-sm p-0.5 hover:bg-background/60"
                    onClick={() => remove(skill)}
                    aria-label={`Remove ${skill}`}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                )}
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
      {disabled ? (
        value.length === 0 ? (
          <p className="text-sm text-muted-foreground">No skills recorded.</p>
        ) : null
      ) : (
        <div className="relative">
          <Input
            id={id}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setOpen(true)}
            onBlur={() => window.setTimeout(() => setOpen(false), 120)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                add(text);
              } else if (e.key === 'Backspace' && text === '' && value.length > 0) {
                remove(value[value.length - 1]!);
              } else if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
            placeholder={placeholder}
            autoComplete="off"
            aria-label={aria['aria-label']}
            aria-describedby={aria['aria-describedby']}
            aria-controls={open && matches.length > 0 ? listId : undefined}
            aria-expanded={open && matches.length > 0}
          />
          {open && matches.length > 0 ? (
            <ul
              id={listId}
              className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
              aria-label="Skill suggestions"
            >
              {matches.map((s) => (
                <li key={s}>
                  <button
                    type="button"
                    className="w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => add(s)}
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <p className="mt-1 text-xs text-muted-foreground">
            Pick from the list or press Enter to add what you typed.
          </p>
        </div>
      )}
    </div>
  );
}
